import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { useEffect, useState } from 'react';

import { sourceKeyOf, type DocumentSource } from './documentSource';
import { fetchDocumentBytes } from './fetchDocumentBytes';

type PdfState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'ready';
      document: PDFDocumentProxy;
      /** The same bytes, as a local `blob:` URL, so the reader can keep a copy
       *  without a second trip to a host that may not answer the same way
       *  twice — a signed URL expires, and a cross-origin one is not a link
       *  the browser will let us name. */
      downloadUrl: string;
    }
  | { status: 'error' };

/**
 * Loads pdf.js and its worker on first use, never at module scope.
 *
 * The library is around a third of the app's own bundle, and most sessions
 * never open a PDF at all — a director signing from a link reads one document
 * and leaves. Importing it lazily keeps that weight off the first paint of
 * every screen, which on a phone on mobile data is the cost that actually
 * shows.
 *
 * The worker goes through `?worker&url` rather than `new URL(...)` so Vite
 * emits it as an ordinary `.js` chunk. pdf.js ships `.mjs` only, and the
 * hosting in front of this app rewrites every unmatched path to `index.html`
 * against an extension allowlist that covers `js` and `css` but not `mjs` —
 * so a `.mjs` worker is answered with the HTML document at 200, the browser
 * refuses it for its MIME type, and the viewer fails on a file it fetched
 * perfectly well. Emitting `.js` keeps the fix in the repo rather than in
 * console config that no redeploy would restore.
 */
const loadPdfJs = async () => {
  const [pdfjs, { default: workerUrl }] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.mjs?worker&url'),
  ]);

  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  return pdfjs;
};

/**
 * Opens a PDF for rendering, given a source.
 *
 * The bytes are fetched rather than handed to pdf.js as a URL, because a URL is
 * frequently not readable: the private documents bucket sends no
 * `access-control-allow-origin`, so a direct read fails CORS no matter what
 * pdf.js does with it. `fetchDocumentBytes` picks the route that works — our
 * own API for an id, the URL itself for something already public — and is the
 * only place a session token is attached.
 *
 * Deliberately not RTK Query: a parsed `PDFDocumentProxy` owns a worker and a
 * transport that must be torn down, which is a lifetime tied to a mounted
 * component rather than to a cache key (§6).
 */
export const usePdfDocument = (source: DocumentSource, isEnabled: boolean): PdfState => {
  const [state, setState] = useState<PdfState>({ status: 'idle' });
  // The source is an object, so a parent re-render hands this a new one every
  // time even when nothing changed. Keying the effect on its identity string is
  // what stops that from refetching and re-parsing the document on each render.
  const sourceKey = sourceKeyOf(source);

  useEffect(() => {
    // An empty key is a document with no address at all, and `fetch('')`
    // resolves against the current page — it would fetch the app's own HTML
    // and hand it to pdf.js rather than reporting that there is nothing here.
    if (!isEnabled || sourceKey === '') return;

    const controller = new AbortController();
    // Revoking on cleanup is not optional: an object URL pins its blob in
    // memory for the lifetime of the document, so a reader who opens the guide
    // on every visit leaks a copy each time.
    let objectUrl = '';
    // The LOADING TASK is what owns teardown, not the document it resolves to:
    // destroying it terminates the worker and releases the document with it.
    // Captured here because an unmount mid-parse must still tear down a task
    // whose promise has not settled yet.
    let task: PDFDocumentLoadingTask | undefined;
    let isCancelled = false;

    const load = async () => {
      setState({ status: 'loading' });

      try {
        const bytes = await fetchDocumentBytes(source, controller.signal);
        // pdf.js takes ownership of the array it parses — it transfers the
        // buffer to its worker and leaves a detached one behind — so the blob
        // is built from its own copy before `getDocument` is handed anything.
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));

        const pdfjs = await loadPdfJs();
        task = pdfjs.getDocument({ data: new Uint8Array(bytes) });
        const document = await task.promise;

        // The component went away while the worker was parsing. Tearing down is
        // not optional — the worker outlives the React tree otherwise.
        if (isCancelled) {
          void task.destroy();
          return;
        }

        setState({ status: 'ready', document, downloadUrl: objectUrl });
      } catch {
        if (!controller.signal.aborted && !isCancelled) setState({ status: 'error' });
      }
    };

    void load();

    return () => {
      isCancelled = true;
      controller.abort();
      void task?.destroy();
      if (objectUrl !== '') URL.revokeObjectURL(objectUrl);
    };
    // `source` is intentionally absent: `sourceKey` is its value identity, and
    // depending on the object itself would refetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by sourceKey, see above
  }, [sourceKey, isEnabled]);

  // Derived rather than held, so a closed viewer never reports a document whose
  // worker the cleanup above has already destroyed.
  if (!isEnabled) return { status: 'idle' };
  // Nothing to fetch means nothing will ever arrive, and `idle` would leave the
  // viewer spinning forever — `error` is what its own state already handles.
  return sourceKey === '' ? { status: 'error' } : state;
};
